import { themeEvents } from "../core/events";

const RUNTIME_KEY = "__rebalancePageflyRuntime";
const PAGEFLY_CONTAINER_SELECTOR = ".pagefly-container";

interface PageflyRuntime {
  cleanup: () => void;
}

interface PageflyRuntimeWindow extends Window {
  [RUNTIME_KEY]?: PageflyRuntime;
}

function callIfFunction(candidate: unknown): boolean {
  if (typeof candidate !== "function") {
    return false;
  }

  try {
    (candidate as () => void)();
    return true;
  } catch {
    return false;
  }
}

function refreshPagefly(reason: string): boolean {
  const hasContainer = Boolean(document.querySelector(PAGEFLY_CONTAINER_SELECTOR));
  const invoked =
    callIfFunction(window.PageFly?.init) ||
    callIfFunction(window.__PAGEFLY__?.init) ||
    hasContainer;

  document.documentElement.setAttribute("data-build-pagefly-adapter", "ready");
  document.documentElement.setAttribute(
    "data-build-pagefly-last-refresh",
    `${reason}:${invoked ? "invoked" : "no-op"}`
  );
  document.documentElement.setAttribute(
    "data-build-pagefly-has-container",
    hasContainer ? "1" : "0"
  );

  window.dispatchEvent(
    new CustomEvent("rebalance:pagefly:refresh", {
      detail: { reason, invoked, hasContainer }
    })
  );

  return invoked;
}

export function initPagefly(): void {
  const runtimeWindow = window as PageflyRuntimeWindow;
  if (runtimeWindow[RUNTIME_KEY]) {
    return;
  }

  let refreshTimer = 0;
  const scheduleRefresh = (reason: string, delayMs = 120): void => {
    if (refreshTimer) {
      window.clearTimeout(refreshTimer);
    }

    refreshTimer = window.setTimeout(() => {
      refreshTimer = 0;
      refreshPagefly(reason);
    }, delayMs);
  };

  const onRouteInitialized = ({ template }: { template: string }): void => {
    if (template === "page" || template === "home" || template === "unknown") {
      scheduleRefresh(`route-initialized:${template}`);
    }
  };
  const unlistenRouteInitialized = themeEvents.on("route:initialized", onRouteInitialized);

  runtimeWindow[RUNTIME_KEY] = {
    cleanup: () => {
      unlistenRouteInitialized();
      if (refreshTimer) {
        window.clearTimeout(refreshTimer);
        refreshTimer = 0;
      }
    }
  };

  scheduleRefresh("adapter-init", 0);
}
